/**
 * OrganizerEditor.js
 *
 * Collapsible in-form editor for the signed-in user's organization profile.
 * Slots into the event edit modal (AdminEventManager, Vendor event manager,
 * etc.) so organizers can update their org details without leaving the
 * event they're editing.
 *
 * Reads from GET /api/organizations/me and writes via PUT /api/organizations/me.
 * The Organiser tab on the public event detail page auto-picks up the
 * changes on next load — no event-level fields need updating.
 *
 * Props:
 *   - defaultOpen: boolean — expand the section by default
 *
 * Assumptions:
 *   - Auth token is in localStorage under 'token' (matches the rest of
 *     AdminEventManager). Sign-in is required — if the endpoint 401s the
 *     card silently hides.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Building2, Save, Loader2, ChevronDown, ChevronUp, Globe, Instagram,
  Twitter, Facebook, ExternalLink,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Handy small wrapper — pulls the token every render because the parent
// signs in / out without unmounting us.
const auth = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('eventism_token');
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

const BLANK = {
  display_name: '',
  bio: '',
  logo_url: '',
  website: '',
  primary_city: '',
  instagram: '',
  twitter: '',
  facebook: '',
};


export default function OrganizerEditor({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [slug, setSlug] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/organizations/me`, auth());
        if (cancelled) return;
        const o = r.data || {};
        setForm({
          display_name: o.display_name || o.legal_name || '',
          bio:          o.bio          || '',
          logo_url:     o.logo_url     || '',
          website:      o.website      || '',
          primary_city: o.primary_city || '',
          instagram:    o.instagram    || '',
          twitter:      o.twitter      || '',
          facebook:     o.facebook     || '',
        });
        setSlug(o.slug || null);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status && status !== 401 && status !== 403 && status !== 404) {
          setError(e?.response?.data?.detail || 'Failed to load organization');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    const t = toast.loading('Saving organizer details…');
    try {
      // Backend `OrganizationUpdate` uses flat social fields (not a nested
      // social_links dict) and `bio`/`primary_city` (not description/city).
      const payload = {
        display_name: form.display_name.trim(),
        bio:          form.bio || '',
        logo_url:     form.logo_url || '',
        website:      form.website || '',
        primary_city: form.primary_city || '',
        instagram:    (form.instagram || '').trim(),
        twitter:      (form.twitter   || '').trim(),
        facebook:     (form.facebook  || '').trim(),
      };
      const r = await axios.put(`${API}/organizations/me`, payload, auth());
      const o = r.data || {};
      setForm((f) => ({
        ...f,
        display_name: o.display_name || payload.display_name,
        bio:          o.bio          ?? payload.bio,
      }));
      setSlug(o.slug || slug);
      toast.success('Organizer profile updated — Organiser tab will refresh on next event view', {
        id: t, duration: 4000,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save organizer', { id: t });
    } finally {
      setSaving(false);
    }
  };

  if (!loading && !error && !form.display_name && !slug) {
    return null;
  }

  return (
    <div
      className="rounded-xl border border-fuchsia-500/25 bg-fuchsia-500/5 overflow-hidden"
      data-testid="organizer-editor"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-3 hover:bg-fuchsia-500/10 transition-colors text-left"
        data-testid="organizer-editor-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="h-4 w-4 text-fuchsia-300 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-fuchsia-200">Organizer profile</p>
            <p className="text-[11px] text-muted-foreground truncate">
              {loading
                ? 'Loading…'
                : form.display_name || 'Update your details — shows on every event\'s Organiser tab'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {slug && open && (
            <a
              href={`/organizations/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-fuchsia-300 hover:text-fuchsia-200 flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
              data-testid="organizer-editor-view-public"
            >
              View public <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {open && (
        <div className="p-4 pt-1 space-y-3 border-t border-fuchsia-500/20" data-testid="organizer-editor-body">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading organizer profile…
            </div>
          )}
          {!loading && error && (
            <p className="text-xs text-red-400 py-2" data-testid="organizer-editor-error">{error}</p>
          )}
          {!loading && !error && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-[11px]">Organizer name *</Label>
                  <Input
                    value={form.display_name}
                    onChange={(e) => update({ display_name: e.target.value })}
                    placeholder="e.g. Denver Wine Collective"
                    data-testid="organizer-editor-name"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Shows as the headline on the Organiser tab of every event you host.
                  </p>
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-[11px]">Description (shows on the Organiser tab)</Label>
                  <Textarea
                    rows={4}
                    value={form.bio}
                    onChange={(e) => update({ bio: e.target.value })}
                    placeholder="A short pitch about your organization — who you are, what you do, how long you've been running events."
                    data-testid="organizer-editor-description"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] flex items-center gap-1">
                    <Globe className="h-3 w-3" /> Website
                  </Label>
                  <Input
                    value={form.website}
                    onChange={(e) => update({ website: e.target.value })}
                    placeholder="https://…"
                    data-testid="organizer-editor-website"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Logo URL</Label>
                  <Input
                    value={form.logo_url}
                    onChange={(e) => update({ logo_url: e.target.value })}
                    placeholder="https://…/logo.png"
                    data-testid="organizer-editor-logo"
                  />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-[11px]">Primary city</Label>
                  <Input
                    value={form.primary_city}
                    onChange={(e) => update({ primary_city: e.target.value })}
                    placeholder="Denver"
                    data-testid="organizer-editor-city"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 pt-1">
                <div className="space-y-1">
                  <Label className="text-[11px] flex items-center gap-1">
                    <Instagram className="h-3 w-3" /> Instagram
                  </Label>
                  <Input
                    value={form.instagram || ''}
                    onChange={(e) => update({ instagram: e.target.value })}
                    placeholder="@handle or full URL"
                    data-testid="organizer-editor-instagram"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] flex items-center gap-1">
                    <Twitter className="h-3 w-3" /> Twitter / X
                  </Label>
                  <Input
                    value={form.twitter || ''}
                    onChange={(e) => update({ twitter: e.target.value })}
                    placeholder="@handle or full URL"
                    data-testid="organizer-editor-twitter"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] flex items-center gap-1">
                    <Facebook className="h-3 w-3" /> Facebook
                  </Label>
                  <Input
                    value={form.facebook || ''}
                    onChange={(e) => update({ facebook: e.target.value })}
                    placeholder="Page name or full URL"
                    data-testid="organizer-editor-facebook"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button
                  type="button"
                  onClick={save}
                  disabled={saving || !form.display_name}
                  size="sm"
                  className="bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white hover:opacity-90"
                  data-testid="organizer-editor-save"
                >
                  {saving
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving…</>
                    : <><Save className="h-3.5 w-3.5 mr-1.5" /> Save organizer profile</>}
                </Button>
                <p className="text-[10px] text-muted-foreground">
                  Changes apply to every event you host — no need to edit events one by one.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
